import { DROP_KINDS, RETYPE_KINDS, BUNDLE_LINE, dropKind, classLabel } from '@/lib/dropKinds.js';
import { formatHumanDate } from '@/lib/format.js';
import { alreadyHome, signedOnProblem } from '@/hooks/useDocumentDrop.js';
import RetypeControl from './RetypeControl.jsx';

// THE DROP DIALOG'S ROWS (paperwork-1 screen 2, D7, A18): one row per dropped file. Each asks what the file is, with one plain
// line under the kind saying what that kind is; asks the kind's second question (which ledger row, which holder; an endorsement
// also asks which lienholder or loss payee it names when the policy has one, Q19); shows the
// name the server will file it as BEFORE filing; never offers a carrier kind for a file the server says is NDEMNIFLY's own
// paper; asks "Does this replace <name>?" when the server says a document of that kind is already filed (asked, never
// inferred); and after filing shows the filed name with "Wrong type? Change it". Every word on a row is the server's answer
// or the kinds list; nothing is derived here. `drop` is useDocumentDrop's state; `exclude` hides kinds a surface does not file.

function txnLabel(t) {
  const when = t.effective_date ? formatHumanDate(t.effective_date) || t.effective_date : 'no effective date';
  const what = t.type === 'cancellation' ? 'Cancellation' : 'Endorsement';
  return `${what} effective ${when}${t.description ? `: ${t.description}` : ''}`;
}

function KindLine({ kind }) {
  const k = dropKind(kind);
  return k ? <p className="text-[12px] text-text-muted" data-kind-line={kind}>{k.label}: {k.line}</p> : null;
}

function SecondQuestion({ e, drop, disabled }) {
  const k = dropKind(e.kind);
  if (!k?.pick) return null;
  if (k.pick === 'transaction') {
    const rows = drop.context?.transactions?.[e.kind] || [];
    return (
      <>
      <label className="flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
        <span>Which change on the ledger?</span>
        <select className="min-h-[32px] min-w-0 max-w-full rounded-card border border-border bg-bg px-2 py-1 text-sm text-text" value={e.transaction_id} disabled={disabled}
          onChange={(ev) => drop.set(e.key, { transaction_id: ev.target.value })} data-pick-transaction={e.key}>
          <option value="">Not on the ledger yet (file it unattached)</option>
          {rows.map((t) => <option key={t.id} value={t.id}>{txnLabel(t)}</option>)}
        </select>
        {drop.context?.error ? <span className="text-warning">{drop.context.error}</span> : null}
      </label>
      {e.kind === 'endorsement' ? <HolderQuestion e={e} drop={drop} disabled={disabled} /> : null}
      </>
    );
  }
  const rows = drop.interests?.rows || [];
  return (
    <label className="flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
      <span>Which holder does it name?</span>
      <select className="min-h-[32px] min-w-0 max-w-full rounded-card border border-border bg-bg px-2 py-1 text-sm text-text" value={e.interest_id} disabled={disabled}
        onChange={(ev) => drop.set(e.key, { interest_id: ev.target.value })} data-pick-interest={e.key}>
        <option value="">Nobody on the interests list</option>
        {rows.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
      </select>
      {drop.interests?.error ? <span className="text-warning">{drop.interests.error}</span> : null}
    </label>
  );
}

// David Q6 (round 2): a signed application asks the day it was signed (the date on the application). Required: the server
// refuses a drop without it, and the window counts from it.
// Review fix, round 2: the input stops at the server's latest day (drop-context signed_on_latest, today Eastern) and a later
// day typed in is said on the row, so one wrong date never refuses the whole drop.
function SignedOnQuestion({ e, drop, disabled }) {
  if (e.kind !== 'application') return null;
  const problem = signedOnProblem(e, drop.context);
  return (
    <label className="flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
      <span>Signed on (the date on the application)</span>
      <input type="date" required max={drop.context?.signed_on_latest || undefined} className="min-h-[32px] rounded-card border border-border bg-bg px-2 py-1 text-sm text-text" value={e.signed_on || ''} disabled={disabled}
        onChange={(ev) => drop.set(e.key, { signed_on: ev.target.value })} data-signed-on={e.key} aria-label={`The day ${e.file.name} was signed`} />
      {problem ? <span className="text-warning" data-signed-on-missing>{problem}</span> : null}
    </label>
  );
}

// Q19 (David 2026-10-31): an endorsement may name a lienholder or loss payee (the loss-payee endorsement), asked only when the
// policy has one; that holder then gets it in the standard set beside the client. "It names none of them" is the default.
function HolderQuestion({ e, drop, disabled }) {
  const holders = (drop.interests?.rows || []).filter((i) => i.is_loss_payee === true);
  if (!holders.length) return null;
  return (
    <label className="flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
      <span>Does it name a lienholder or loss payee?</span>
      <select className="min-h-[32px] min-w-0 max-w-full rounded-card border border-border bg-bg px-2 py-1 text-sm text-text" value={e.interest_id} disabled={disabled}
        onChange={(ev) => drop.set(e.key, { interest_id: ev.target.value })} data-pick-holder={e.key}>
        <option value="">It names none of them</option>
        {holders.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
      </select>
    </label>
  );
}

function ReplaceQuestion({ e, drop, disabled }) {
  const cands = e.preview?.replaces || [];
  if (!cands.length) return null;
  return (
    <fieldset className="rounded-card border border-border px-2.5 py-1.5" data-replace-question={e.key}>
      <legend className="px-1 text-[12px] font-medium text-text">Does this replace {cands.length === 1 ? cands[0].name : 'one already filed'}?</legend>
      <label className="flex min-h-[32px] items-center gap-2 text-[13px] text-text">
        <input type="radio" name={`replaces-${e.key}`} checked={e.replaces === 'none'} disabled={disabled} onChange={() => drop.set(e.key, { replaces: 'none' })} />
        No, it is another document
      </label>
      {cands.map((c) => (
        <label key={c.id} className="flex min-h-[32px] items-center gap-2 text-[13px] text-text">
          <input type="radio" name={`replaces-${e.key}`} checked={e.replaces === c.id} disabled={disabled} onChange={() => drop.set(e.key, { replaces: c.id })} />
          Yes, it replaces {c.name}{c.original_filename ? <span className="text-text-dim"> ({c.original_filename})</span> : null}
        </label>
      ))}
    </fieldset>
  );
}

function Filed({ e, drop }) {
  const r = e.result;
  const offered = RETYPE_KINDS.filter((k) => !(e.preview?.our_paper && k.carrier));
  if (!r) return null;
  if (!r.ok) {
    return (
      <div className="text-[12px] text-danger" data-filed-error={r.code || 'failed'}>
        {r.error || 'not filed'}
        {r.document ? <span className="block text-text-muted">Filed as {r.document.name}</span> : null}
      </div>
    );
  }
  if (r.quote) return <p className="text-[12px] text-text-muted" data-filed-quote>Sent to the quote review</p>;
  const d = r.document;
  if (!d) {
    return (
      <div className="text-[12px] text-text-muted" data-filed-application>
        Filed as {r.name} (signed application)
        {r.converted_from ? <span className="block" data-filed-converted={r.converted_from}>Converted from {r.converted_from_label || 'an image'} to a PDF; the original image is kept as {r.original?.name || 'its own document'}.</span> : null}
      </div>
    );
  }
  // A document that replaced another keeps its kind (placeDocument refuses IN_A_REPLACEMENT_CHAIN): the row says what it
  // replaced and offers no re-type the door would refuse (L1b-B4, Chrome pass).
  const replaced = r.steps?.superseded ? (e.preview?.replaces || []).find((c) => c.id === r.steps.superseded) || { name: 'the earlier copy' } : null;
  if (replaced) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-[12px]" data-filed={d.id}>
        <span className="text-text">Filed as {d.name}</span>
        <span className="text-text-muted">({classLabel(d.class)})</span>
        <span className="basis-full text-text-muted" data-filed-replaces={r.steps.superseded}>It replaces {replaced.name}{replaced.original_filename ? ` (${replaced.original_filename})` : ''}; a document that replaced another keeps its kind.</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px]" data-filed={d.id}>
      <span className="text-text">Filed as {d.name}</span>
      <span className="text-text-muted">({classLabel(d.class)})</span>
      <RetypeControl doc={d} dealId={drop.dealId} kinds={offered} disabled={e.retyping} latest={drop.context?.signed_on_latest || null} onRetype={(kind, extra) => drop.retype(e.key, kind, extra)} selectAttr="data-retype" />
      {e.retypeError ? <span className="basis-full text-danger" data-retype-error>{e.retypeError}</span> : null}
    </div>
  );
}

export default function DocumentDropRows({ drop, exclude = [], disabled = false }) {
  return (
    <div className="space-y-3" data-drop-rows>
      <p className="text-[12px] text-text-dim">{BUNDLE_LINE}</p>
      {drop.noPolicy ? <p role="alert" className="rounded-card border border-warning/40 bg-warning-soft/30 px-3 py-2 text-[13px] text-text" data-drop-no-policy>{drop.noPolicy}</p> : null}
      {drop.entries.map((e) => {
        const ours = e.preview?.our_paper || null;
        const kinds = DROP_KINDS.filter((k) => !exclude.includes(k.kind) && !(ours && k.carrier));
        // The server's rule for THIS file (preview kind_rules, utils/document-kind-rules.js): a kind it cannot be is shown
        // disabled, and why is said in words under the choice (a disabled option's tooltip never reaches a phone).
        const rules = e.preview?.kind_rules || {};
        const refusedKinds = kinds.filter((k) => rules[k.kind]?.offered === false);
        // Bytes already filed with every fact stated: nothing to ask and nothing to post, only "Remove this file" (L1b-B4).
        const home = alreadyHome(e);
        const done = !!e.result?.ok || home;
        return (
          <div key={e.key} className="space-y-1.5 rounded-card border border-border p-2.5" data-drop-row={e.key}>
            <div className="flex flex-wrap items-center gap-2">
              {/* The whole name the browser handed over, wrapped, never cut (a downloaded file the server sent unnamed arrives as
                  the browser's "document (17)"; it is shown as is). */}
              <span className="min-w-0 flex-1 break-words text-sm text-text [overflow-wrap:anywhere]" title={e.file.name} data-drop-filename={e.key}>{e.file.name}</span>
              {e.result?.ok ? null : (
                <button type="button" className="min-h-[32px] px-1.5 text-[12px] text-text-dim hover:text-accent" disabled={disabled} onClick={() => drop.remove(e.key)} data-remove={e.key} aria-label={`Remove ${e.file.name} from this drop`}>Remove this file</button>
              )}
              {done ? null : (
                <select className="min-h-[32px] min-w-0 max-w-full rounded-card border border-border bg-bg px-2 py-1 text-sm text-text" value={e.kind || ''} disabled={disabled}
                  onChange={(ev) => drop.set(e.key, { kind: ev.target.value, transaction_id: '', interest_id: '', signed_on: '', replaces: undefined })} data-kind-select={e.key} aria-label={`What ${e.file.name} is`}>
                  <option value="" disabled>What is it?</option>
                  {kinds.map((k) => <option key={k.kind} value={k.kind} title={rules[k.kind]?.why || k.line} disabled={rules[k.kind]?.offered === false} data-kind-option={k.kind}>{k.label}{rules[k.kind]?.offered === false ? ' (not for this file)' : ''}</option>)}
                </select>
              )}
            </div>
            {!done && refusedKinds.length ? (
              <ul className="space-y-0.5 text-[12px] text-text-muted" data-kind-refused={e.key}>
                {refusedKinds.map((k) => <li key={k.kind} data-kind-refused-kind={k.kind}>{k.label} is not offered: {rules[k.kind].why}.</li>)}
              </ul>
            ) : null}
            {!done && rules[e.kind]?.converts ? <p className="text-[12px] text-text-muted" data-converts={e.key}>This image is converted to a PDF when it is filed; the original image is kept beside it.</p> : null}
            {!done ? <KindLine kind={e.kind} /> : null}
            {ours ? <p className="text-[12px] text-warning" data-our-paper={ours.rule}>This file is NDEMNIFLY&apos;s own paperwork ({ours.what}), not a carrier document.{home ? '' : ' It can be filed as a signed application or as something else.'}</p> : null}
            {home && e.preview.already_filed?.settled ? <p className="text-[12px] text-text" data-already-home={e.preview.already_filed.id}>Already filed as {e.preview.already_filed.name || classLabel(e.preview.already_filed.class)}. Nothing to file; remove it from this drop.</p> : null}
            {home && !e.preview.already_filed?.settled && e.preview.application_on_file ? <p className="text-[12px] text-text" data-application-on-file={e.preview.application_on_file.id}>These bytes are already uploaded to this deal as {e.preview.application_on_file.name || 'a signed application'}. Nothing to file; remove it from this drop.</p> : null}
            {!done ? <SecondQuestion e={e} drop={drop} disabled={disabled} /> : null}
            {!done ? <SignedOnQuestion e={e} drop={drop} disabled={disabled} /> : null}
            {/* A preview that failed says so on its row before any kind is chosen: the file is kept, uncounted (review of
                2026-11-02, finding I2). With a kind, the "Will be filed as" line below says it instead. */}
            {!done && e.preview?.error && !(e.kind && e.kind !== 'quote') ? <p className="text-[12px] text-warning" data-not-checked={e.key}>{e.preview.error}</p> : null}
            {!done && e.kind && e.kind !== 'quote' ? (
              <p className="text-[12px] text-text-muted" data-will-file-as={e.key}>
                {e.sha === null ? 'Reading the file' : e.preview?.error ? <span className="text-warning">{e.preview.error}</span> : e.preview?.name ? <>Will be filed as: <span className="text-text">{e.preview.name}</span></> : 'Naming it'}
              </p>
            ) : null}
            {!done && e.preview?.already_filed ? <p className="text-[12px] text-text-muted" data-already-filed>These bytes are already filed as {e.preview.already_filed.name || classLabel(e.preview.already_filed.class)}.</p> : null}
            {!done ? <ReplaceQuestion e={e} drop={drop} disabled={disabled} /> : null}
            <Filed e={e} drop={drop} />
          </div>
        );
      })}
    </div>
  );
}
